import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { ALFRED_IN_OUTREACH_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of Alfred-on-Outreach — `alfredInSymphonyOutreach`.
 *
 * Variant (`true`): the Outreach tab draws Alfred (the AI phone receptionist) above iMessage in place of the
 * coming-soon Phone row (`lib/outreach-alfred.ts`). Control (`false`): today's list, byte for byte.
 *
 * It FAILS CLOSED to CONTROL, like the sibling gates: a gate that has not answered (or errored) shows the
 * list every user already has rather than swapping a row for one account and not the next.
 *
 * Unlike the sibling gates, a FAILED read is not cached: the same value moves Alfred between the Tools tab and
 * the Outreach row (`listedOnTools`), so a latched failure would pin a ramped account to the Tools card for the
 * whole page session. The next mount asks again; a real answer stays cached.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value (`toggleOverride` is consulted
 * first), and that override is inert without a dev-tools grant, so a real user always gets the conducted
 * value. Forcing it On still draws nothing unless the server also sent an Alfred connector entry.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function alfredInOutreachEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(ALFRED_IN_OUTREACH_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/alfred-in-outreach`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) {
        cached = null;
        return false;
      }
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control — this time. Not latched.
      cached = null;
      return false;
    }
  })();
  return cached;
}

/**
 * The arm, for rendering. Starts false — today's list — so a user outside the release never sees the
 * Phone row swap out and back.
 */
export function useAlfredInOutreachEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void alfredInOutreachEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
